import { useRouter } from '#imports'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { SHOPPING_ASSISTANT, SHOPPING_ASSISTANT_NEW } from '../routes.constants'

import { useJSBridge } from './useJSBridge'
import { useShoppingAssistantActions } from './useShoppingAssistantActions'
import type { WebviewShoppingAssistantHost } from './useShoppingAssistantHost.injection-key'

/**
 * Builds the host adapter for the native-app webview shell. Wraps the JS bridge
 * (native dialogs, close, cart notifications, inbound messages) and reflects the
 * created chat id in the URL.
 *
 * Must be called during a component setup: useJSBridge registers
 * mounted/unmounted listeners on window.JSBridge.
 */
export function useWebviewShoppingAssistantHost(): WebviewShoppingAssistantHost {
  const router = useRouter()
  const logger = useLogger()

  const bridge = useJSBridge()
  const { close, newChat, addToCart } = useShoppingAssistantActions(bridge)

  // Reflect the freshly created chat id in the URL without a Vue Router
  // navigation (history.replaceState avoids a remount; preserve history.state
  // per Vue Router docs). URL sync is purely cosmetic — a failure here must
  // never block using the created chat (which matters for deep-link seeded
  // launches), so swallow and log it.
  function syncChatId(id: string) {
    try {
      const newPath = router.resolve({
        name: SHOPPING_ASSISTANT,
        params: { chatId: id },
      }).href
      window.history.replaceState(window.history.state, '', newPath)
    } catch (error) {
      logger.error('[SHOPPING_ASSISTANT] Failed to sync chat id to URL', {
        error: error as Error,
        owners: ['bot-group-ai-shopping-assistant-front'],
      })
    }
  }

  function goToNewChat() {
    router.push({ name: SHOPPING_ASSISTANT_NEW })
  }

  return {
    ready: bridge.sendReady,
    receivedEvent: bridge.receivedEvent,
    clearReceivedEvent: bridge.clearReceivedEvent,
    close,
    newChat,
    addToCart,
    syncChatId,
    goToNewChat,
  }
}
